import { FC, useMemo } from 'react';
import CustomDonutChart from './CustomDonutChart';
import { ResponseActiveOrdersGraphType } from 'api/orders/schemas';

interface MetricsProps {
  metrics: ResponseActiveOrdersGraphType;
  onClick: (status: string) => void;
}

const Metrics: FC<MetricsProps> = ({ metrics, onClick }) => {
  const buildDonutChartData = (
    statuses: ResponseActiveOrdersGraphType['statuses'],
  ) => {
    const predefinedColorsMap: Record<string, string> = {
      'Needs attention!': '#FF315E',
      'On hold': '#3B3EBE',
      Submitted: '#A7FFB2',
      'In progress': '#9796F3',
      'Pending Granite': '#3D79EF',
      'Pending site survey': '#FCDF56',
      Construction: '#FBB979',
      Confirmed: '#6C9DFF',
      Complete: '#47A853',
    };

    const predefinedOrder = [
      'Needs attention!',
      'On hold',
      'Submitted',
      'In progress',
      'Pending Granite',
      'Pending site survey',
      'Construction',
      'Confirmed',
      'Complete',
    ];

    const predefinedKeys = Object.keys(predefinedColorsMap);

    const dataWithPredefinedColors = statuses.map((status, index) => {
      const isExtraServiceType = !predefinedKeys.includes(status.status);
      const hue = (index * 137.5) % 360;
      const backgroundColor = `hsl(${hue}, 50%, 50%)`;
      return {
        x: status.status,
        y: status.count,
        backgroundColor: isExtraServiceType
          ? backgroundColor
          : predefinedColorsMap[status.status],
      };
    });

    // Sort based on predefined order
    return dataWithPredefinedColors.sort((a, b) => {
      const aIndex = predefinedOrder.indexOf(a.x);
      const bIndex = predefinedOrder.indexOf(b.x);

      // If both are in predefined order, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one is in predefined order, prioritize the predefined one
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // If neither is in predefined order, sort alphabetically
      return a.x.localeCompare(b.x);
    });
  };

  const servicesChartData = useMemo(
    () => (metrics ? buildDonutChartData(metrics.statuses) : []),
    [metrics],
  );

  return (
    <CustomDonutChart
      data={servicesChartData}
      scrollLegendOnPieHover={true}
      total={metrics.total}
      onClick={(datum) => onClick(datum.x)}
    />
  );
};

export default Metrics;
